import { useLocation } from 'react-router-dom';
import { Search, Bell, ChevronRight, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../lib/theme';

interface TopBarProps {
  onSearchClick: () => void;
}

const ROUTE_LABELS: Record<string, string[]> = {
  '/dashboard':     ['Platform', 'Dashboard'],
  '/repositories':  ['Platform', 'Repositories'],
  '/architecture':  ['Platform', 'Architecture'],
  '/knowledge':     ['Platform', 'Knowledge Base'],
  '/conversations': ['Platform', 'Conversations'],
  '/integrations':  ['Configuration', 'Integrations'],
  '/team':          ['Configuration', 'Team'],
  '/settings':      ['Configuration', 'Settings'],
};

export function TopBar({ onSearchClick }: TopBarProps) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const crumbs = ROUTE_LABELS[location.pathname] ?? ['Platform'];

  return (
    <header
      className="h-[52px] flex-shrink-0 flex items-center justify-between px-5 border-b backdrop-blur-sm"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--s0) 90%, transparent)',
        borderColor: 'var(--b-subtle)',
      }}
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm min-w-0">
        {crumbs.map((crumb, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-t4 flex-shrink-0" />}
            <span className={i === crumbs.length - 1 ? 'text-t1 font-medium truncate' : 'text-t4 truncate'}>
              {crumb}
            </span>
          </span>
        ))}
      </nav>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        {/* CMD+K search */}
        <button
          onClick={onSearchClick}
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-t3 transition-all group"
          style={{ border: '1px solid var(--b-subtle)', backgroundColor: 'var(--overlay-sm)' }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'var(--overlay-md)';
            e.currentTarget.style.borderColor = 'var(--b-default)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'var(--overlay-sm)';
            e.currentTarget.style.borderColor = 'var(--b-subtle)';
          }}
        >
          <Search className="w-3.5 h-3.5" />
          <span className="text-xs">Search anything</span>
          <span className="flex items-center gap-0.5 ml-1">
            <kbd className="text-[10px] px-1 py-0.5 rounded font-mono leading-none text-t4"
              style={{ backgroundColor: 'var(--overlay-md)', border: '1px solid var(--b-subtle)' }}>⌘</kbd>
            <kbd className="text-[10px] px-1 py-0.5 rounded font-mono leading-none text-t4"
              style={{ backgroundColor: 'var(--overlay-md)', border: '1px solid var(--b-subtle)' }}>K</kbd>
          </span>
        </button>

        {/* Mobile search */}
        <button
          onClick={onSearchClick}
          className="sm:hidden p-2 rounded-lg text-t3 transition-all"
          style={{ border: '1px solid var(--b-subtle)' }}
        >
          <Search className="w-4 h-4" />
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-t3 transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark'
            ? <Sun className="w-4 h-4" />
            : <Moon className="w-4 h-4" />
          }
        </button>

        {/* Notifications */}
        <button
          className="relative p-2 rounded-lg text-t3 transition-colors"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <Bell className="w-4 h-4" />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--t1)' }} />
        </button>
      </div>
    </header>
  );
}
